import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, AlertCircle, ArrowUpRight, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { assets } from "@/lib/assets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const field =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-navy placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] duration-300 focus:border-emerald-brand focus:ring-4 focus:ring-emerald-brand/10";

export const QuoteDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const reset = () => {
    setForm({ name: "", email: "", phone: "", company: "", message: "" });
    setErrors({});
    setStatus("idle");
  };

  const handleOpenChange = (v) => {
    if (!v) setTimeout(reset, 250);
    onOpenChange(v);
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.email.trim()) e.email = "Please enter your email.";
    else if (!emailRe.test(form.email)) e.email = "Please enter a valid email address.";
    if (!form.message.trim()) e.message = "Tell us what you need a quote for.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onChange = (k) => (ev) => {
    setForm((f) => ({ ...f, [k]: ev.target.value }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, { ...form, source: "quote_request" });
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="quote-dialog"
        className="max-w-xl p-0 overflow-hidden border-none rounded-2xl gap-0"
      >
        <div className="bg-navy-900 grain px-7 py-6 relative">
          <img src={assets.logoWhite} alt="AGE-T" className="h-8 w-auto mb-3" />
          <DialogHeader className="space-y-1 text-left">
            <DialogTitle className="font-heading text-2xl text-white tracking-tight">
              Request a Quote
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Tell us about your business and we'll get back to you with a tailored proposal.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-7">
          <AnimatePresence mode="wait">
            {status === "success" ? (
              <motion.div
                key="ok"
                data-testid="quote-success"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-8"
              >
                <div className="mx-auto grid place-items-center h-16 w-16 rounded-full bg-emerald-brand/10 text-emerald-brand">
                  <CheckCircle2 size={34} />
                </div>
                <h3 className="mt-5 font-heading font-bold text-xl text-navy">
                  Thanks — we'll be in touch shortly.
                </h3>
                <p className="mt-2 text-slate-500 text-sm">
                  Your quote request has been received. We appreciate you reaching out to AGE-T.
                </p>
                <button
                  onClick={() => handleOpenChange(false)}
                  data-testid="quote-close"
                  className="mt-7 inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white hover:-translate-y-0.5 transition-transform"
                >
                  Done
                </button>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                onSubmit={onSubmit}
                data-testid="quote-form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                noValidate
              >
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-1.5">Name *</label>
                    <input type="text" value={form.name} onChange={onChange("name")} data-testid="quote-input-name" className={field} placeholder="Your name" />
                    {errors.name && <p data-testid="quote-error-name" className="mt-1 text-xs text-red-500 flex items-center gap-1"><AlertCircle size={12} />{errors.name}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-1.5">Email *</label>
                    <input type="email" value={form.email} onChange={onChange("email")} data-testid="quote-input-email" className={field} placeholder="you@example.com" />
                    {errors.email && <p data-testid="quote-error-email" className="mt-1 text-xs text-red-500 flex items-center gap-1"><AlertCircle size={12} />{errors.email}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-1.5">Phone</label>
                    <input type="tel" value={form.phone} onChange={onChange("phone")} data-testid="quote-input-phone" className={field} placeholder="Optional" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-1.5">Company</label>
                    <input type="text" value={form.company} onChange={onChange("company")} data-testid="quote-input-company" className={field} placeholder="Optional" />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-semibold text-navy mb-1.5">What do you need a quote for? *</label>
                  <textarea rows={4} value={form.message} onChange={onChange("message")} data-testid="quote-input-message" className={`${field} resize-none`} placeholder="Briefly describe the services you're interested in" />
                  {errors.message && <p data-testid="quote-error-message" className="mt-1 text-xs text-red-500 flex items-center gap-1"><AlertCircle size={12} />{errors.message}</p>}
                </div>

                {status === "error" && (
                  <p data-testid="quote-error" className="mt-4 flex items-center gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
                    <AlertCircle size={16} /> Something went wrong. Please try again or call us directly.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  data-testid="quote-submit"
                  className="group mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald-brand px-8 py-3.5 text-sm font-bold uppercase tracking-widest text-navy-900 transition-[transform,box-shadow,background-color,opacity] duration-300 hover:-translate-y-0.5 hover:bg-emerald-light hover:shadow-[0_16px_32px_-12px_rgba(16,185,129,0.6)] disabled:opacity-60"
                >
                  {status === "loading" ? "Sending..." : "Submit Request"}
                  <Send size={15} strokeWidth={2.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
};
